/** Charts class for use in charts module */

import { EventEmitter, OnDestroy, OnInit } from '@angular/core'
import { Subject } from 'rxjs'
import { IMapTemplate } from '../csv-mapper/csv-map.class'

export class Charts {
    constructor() { }

    /**
     * Generates option property for line/bar chart
     * @param opt Basic option for generating line/bar chart
     * See https://echarts.apache.org/examples/en/editor.html?c=area-basic
     */
    static genLineBarOpt(opt: IBasicOpt) {
        const LINEBAROPT = {
            tooltip: {
                trigger: 'item',
                /** Sample for formatter */
                // formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            xAxis: {
                name: '',
                type: 'category',
                boundaryGap: false,
                data: []
            },
            yAxis: {
                name: '',
                type: 'value',
                boundaryGap: false,
                data: []
            },
            series: []
        }

        const { visual, series } = opt
        const finalOpt = { ...LINEBAROPT }

        if (!series || !series.length)
            console.warn('Data is missing. Chart will not show up')
        else {
            finalOpt.series = [...series]

            finalOpt.tooltip = { ...finalOpt.tooltip, ...visual.tooltip }

            /** START:PCPIS - Processes color property in series */
            let colors
            if (series.some(srs => srs.color)) {
                colors = finalOpt.series.map(srs => {
                    const color = srs.color
                    delete srs.color
                    return color
                })

                if (finalOpt.series.some(srs => srs.color))
                    throw new Error('Series still has color. This should not happen')
            }
            if (colors?.length)
                finalOpt['color'] = colors
            /** END:PCPIS - Processes color property in series */

            let hasBar = 0
            for (let i = 0; i < series.length; i++) {
                const serie = series[i]
                if (serie.type === 'bar') {
                    hasBar++
                }
            }

            if (hasBar === series.length) {
                visual.boundaryGap = true
            }
            else if (hasBar && hasBar < series.length) {
                console.warn(`Series with type 'bar' will require boundaryGap to be displayed properly`)
            }
        }

        if (visual) {
            finalOpt.xAxis.name = visual.xaxisname || ''
            finalOpt.yAxis.name = visual.yaxisname || ''

            if (visual.isHorizontal) {
                finalOpt.xAxis.type = 'value'
                finalOpt.yAxis.type = 'category'
                delete finalOpt.xAxis.data
                finalOpt.yAxis.boundaryGap = !!visual.boundaryGap
                finalOpt.yAxis.data = visual.categoryData
            }
            else {
                finalOpt.xAxis.boundaryGap = !!visual.boundaryGap
                finalOpt.xAxis.data = visual.categoryData
                delete finalOpt.yAxis.data
            }

            if (visual.xAxis)
                finalOpt.xAxis = { ...visual.xAxis, ...finalOpt.xAxis }
            if (visual.yAxis)
                finalOpt.yAxis = { ...visual.yAxis, ...finalOpt.yAxis }

            if (visual.grid) {
                finalOpt['grid'] = visual.grid
            }

            if (visual.borderRadius)
                finalOpt.series.map(serie => {
                    const itemStyle = {
                        barBorderRadius: visual.borderRadius
                    }

                    if (serie.type === 'bar') {
                        serie['itemStyle'] = { ...(serie['itemStyle'] || {}), ...itemStyle }
                    }
                })
        }
        return finalOpt
    }

    /**
     * Generates option for a Pie chart
     * See https://echarts.apache.org/examples/en/editor.html?c=pie-doughnut
     */
    static genPieOpt(pieOpt: IPieOpt): {} {
        const PIEOPT = {
            tooltip: {
                trigger: 'item',
                /** Sample for formatter */
                // formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
                orient: 'vertical',
                left: 10,
                data: []
            },
            series: []
        }

        /** Options that are not meant to be change */
        const SERIESOPT = {
            name: '',
            type: 'pie',
            emphasis: {
                label: {
                    show: true,
                    fontSize: '30',
                    fontWeight: 'bold'
                }
            },
            labelLine: {
                show: true
            }
        }

        const { visual, name, data } = pieOpt

        const series = { ...SERIESOPT }
        const finalPieOpt = { ...PIEOPT }

        if (visual) {
            const { isDoughnut, label } = visual
            if (isDoughnut) series['radius'] = ['50%', '70%']

            if (label) {
                series['label'] = {}
                series['labelLine']['show'] = !!label.hasLine
                series['label']['show'] = label.show
            }
        }

        /** No named pie chart is possible */
        if (name) {
            series.name = name
        }

        /** Processes data */
        if (!data) {
            console.warn('Data is missing. Pie will not show up')
        }
        else {
            series['data'] = data
            /** Generate legend values */
            if (visual?.label?.show)
                finalPieOpt.legend.data = data.map(dat => dat.name)
        }

        finalPieOpt.series = [series]
        return finalPieOpt
    }

    /**
     * Generates bar chart with Radial option (polar)
     * See https://echarts.apache.org/examples/en/editor.html?c=bar-polar-stack
     */
    static genRadialOpt() { }
}

/**
 * Due to severity colors having no priority, this method attempts to help sort by priority
 * @param array Array of object with a color key containing the hex color string OR array of hex colors
 * @param nameKey optional color key for array of object
 * @param desc Descending?
 * Returns new object with
 * 
 * Init: Colors sorted by color hex
 * 30-11-2020: Colors sorted by name
 */
export const sortBySeverityColors = (array: (any | string)[], nameKey?: string, desc?: boolean) => {
    // const sortedColors = ['#B7D0A5', '#EDD773', '#E4B676', '#E69390']
    // const sortedColors = ['safe', 'low', 'medium', 'critical']
    //     , clean = (str: string) => (str || '').toLowerCase().trim()
    const sortedColors = ['safe', 'low', 'medium', 'critical']
        , clean = (str: string) => (str || '').toLowerCase().trim()

    let sortedArr = []
    if (nameKey) {
        array.map(ele => {
            const colorIdx = sortedColors.indexOf(clean(ele[nameKey]))

            if (colorIdx > -1) {
                sortedArr[desc ? ((sortedColors.length - 1) - colorIdx) : colorIdx] = ele
            }
        })
    }
    else {
        array.map(color => {
            const colorIdx = sortedColors.indexOf(clean(color))
            if (colorIdx > -1) {
                sortedArr[desc ? ((sortedColors.length - 1) - colorIdx) : colorIdx] = color
            }
        })
    }
    return sortedArr.filter(ele => ele)
}

export interface IBasicOpt {
    visual: {
        xaxisname?: string
        /** Additional xAxis property from echarts */
        xAxis?: any
        yaxisname?: string
        /** Additional yAxis property from echarts */
        yAxis?: any
        /**
         * By default the x-axis is the category, while the value goes to y-axis.
         * If true, x-axis is the value, while y-axis is the category (eg: horizontal bar chart)
         */
        isHorizontal?: boolean
        boundaryGap?: boolean
        /** Data that goes to which ever axis that is type 'category' */
        categoryData?: any[]
        /** use example: https://echarts.apache.org/examples/en/editor.html?c=bar-y-category-stack */
        grid?: any
        /** Overrides default tooltip property */
        tooltip?: any
        /** Only applies to series type 'bar' */
        borderRadius?: number
    }
    /** One series has one color */
    series: {
        type: 'line' | 'bar' | string
        name?: string
        /** Stacking will occur for series with the same name */
        stack?: string
        smooth?: boolean
        /** CSS color */
        color?: string
        data: (number | string | any)[]
        label?: any
        showBackground?: boolean
        backgroundStyle?: {
            color?: string,
            /** Similar to visual.borderRadius */
            barBorderRadius?: number
        }
    }[]
}

export interface IPieOpt {
    visual: {
        isDoughnut?: boolean
        label?: {
            show?: boolean
            hasLine?: boolean
        }
    }
    name: string
    data: {
        value: string | number,
        name: string
    }[]
}

/**
 * Interface to use for all Chart Modules
 */
export interface IChartModule extends OnInit, OnDestroy {

    /**
     * Unique ID for the chart instance (to avoid duplicated instance)
     */
    id: string

    /**
     * Has to be static
     */
    SCHEMA: IChartSchema

    /**
     * Need to be @Input
     */
    data: any[]
    _data: any[]

    /**
     * Need to be @Input
     * Mapping reference that will be used to pair Data to SCHEMA
     */
    mapping: any
    _mapping: any

    chartClicked: EventEmitter<any>

    /** Observable to trigger changes when mapping and data changes */
    initComp$: Subject<boolean>

    /** Unsubber */
    unsubber: Subject<boolean>

    /**
     * Method to generate chart from _data and _mapping
     */
    generateChart: () => void

    onChartClicked: (event: any) => void
}

/**
 * Interface for the layout of component
 */
export interface ILayout {
    /** 
     * Represent the horizontal or vertical layout 
     * Defaults to ChartLayout.horizontal
     */
    orient?: ChartLayout
    /**
     * For developer use
     * If true, settings icon will be present above the charts
     */
    isDev?: boolean
}

export interface IChartSchema extends IMapTemplate {
    /** Additional setting for the chart UI? */
    settings?: any
}


export enum ChartLayout {
    horizontal = 'horiz',
    vertical = 'vert'
}

export enum ChartTypes {
    /** Basic Line Bar */
    basicLB = 'basicLineBar',
    /** Basic Pie */
    basicPie = 'basicPie',
    /** Basic S-curve */
    basicScurve = 'basicScurve',
    /** Box Chart */
    boxChart = 'boxChart'
}